import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Logo image URL
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_datagen-avalon/artifacts/udefoijj_photo_2025-05-26_19-31-49%20%281%29.png';

// Company info
const companyInfo = {
  name: 'Avalon X Media GmbH',
  address: 'Schozacherstr. 40, 70437 Stuttgart, Germany',
  email: 'service@avalonxmedia.com',
  phone: '+49 172 3541 325',
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerLinks = {
    company: [
      { name: t.nav.aboutUs, path: '/about' },
      { name: t.nav.services, path: '/services' },
      { name: t.nav.forPartners, path: '/advertisers' },
      { name: t.nav.contact, path: '/contact' },
    ],
    services: [
      { name: 'Co-Registration', path: '/services#co-reg' },
      { name: 'Email Marketing', path: '/services#email' },
      { name: 'SMS Funnels', path: '/services#sms' },
      { name: 'Lead Acquisition', path: '/services#sweepstakes' },
    ],
    legal: [
      { name: t.footer.privacyPolicy, path: '/privacy-policy' },
      { name: t.footer.imprint, path: '/imprint' },
      { name: t.footer.termsOfService, path: '/agb' },
    ],
  };

  return (
    <footer className="bg-slate-950 text-slate-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Avalon X Media Logo" className="w-9 h-9 md:w-10 md:h-10" />
              <div className="flex flex-col">
                <span className="font-semibold text-white text-lg tracking-tight">
                  Avalon X Media
                </span>
                <span className="text-xs text-slate-400 -mt-0.5">GmbH</span>
              </div>
            </Link>
            <div className="space-y-3 mt-6">
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{companyInfo.address}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="hover:text-emerald-400 transition-colors">
                  {companyInfo.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="hover:text-emerald-400 transition-colors">
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.company}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.servicesTitle}</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.legal}</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {currentYear} {companyInfo.name}. {t.footer.allRightsReserved}
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-all duration-200"
                aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition-all duration-200"
                aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
